import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";
import { workspaceService } from "@/services/workspace";
import type { WorkspaceListParams } from "@/services/workspace";

import type {
  CreateWorkspaceReq,
  UpdateWorkspaceReq,
  UpsertCategoryBonusRuleReq,
  UpsertNotificationScheduleReq,
} from "@artisancode/api-types";

export function useWorkspaces(params?: WorkspaceListParams) {
  return useQuery({
    queryKey: queryKeys.workspaces.list(params as Record<string, unknown>),
    queryFn: () => workspaceService.list(params),
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWorkspaceReq) => workspaceService.create(body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.workspaces.all }),
  });
}

export function useUpdateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & UpdateWorkspaceReq) =>
      workspaceService.update(id, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.workspaces.all }),
  });
}

export function useDeleteWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workspaceService.delete(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.workspaces.all }),
  });
}

export function useWorkspaceBranding() {
  return useQuery({
    queryKey: queryKeys.workspaces.branding(),
    queryFn: () => workspaceService.branding(),
  });
}

export function useWorkspaceBonusRules(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.workspaces.bonusRules(workspaceId),
    queryFn: () => workspaceService.getBonusRules(workspaceId),
    enabled: !!workspaceId,
  });
}

export function useUpdateWorkspaceBonusRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      workspaceId,
      rules,
    }: {
      workspaceId: string;
      rules: UpsertCategoryBonusRuleReq[];
    }) => workspaceService.updateBonusRules(workspaceId, rules),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bonusRules(variables.workspaceId),
      });
    },
  });
}

export function useWorkspacePayrollReminderSettings(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.workspaces.notificationSchedule(
      workspaceId,
      "payroll_reminder",
    ),
    queryFn: () =>
      workspaceService.getNotificationSchedule(workspaceId, "payroll_reminder"),
    enabled: !!workspaceId,
  });
}

export function useUpdateWorkspacePayrollReminderSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      workspaceId,
      ...body
    }: { workspaceId: string } & UpsertNotificationScheduleReq) =>
      workspaceService.updateNotificationSchedule(
        workspaceId,
        "payroll_reminder",
        body,
      ),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.notificationSchedule(
          variables.workspaceId,
          "payroll_reminder",
        ),
      });
    },
  });
}
